import { type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react'
import { parseAsStringLiteral, useQueryState, type UseQueryStateReturn } from 'nuqs'
import { PreferencesForm } from 'app/components/preferences'
import { EntityTabs, type TSegmentedControlOption } from 'app/components/pickers'
import { PermissionTooltip } from 'app/components/permissions/PermissionTooltip'
import { PermissionContent } from 'app/components/permissions'
import { NotReady } from 'app/components/NotReady'
import { PrefetchedTeamInfo } from 'app/components/entityInfo/TeamInfo'
import { TButton } from 'app/components/buttons'
import {
  type CompanyData,
  isEmpty,
  pluralize,
  type TeamData,
  type TeamId,
  type TeamLevel,
  type TinyTeam,
} from '@terros/common'
import { Alert, Stack } from '@mantine/core'
import { getLevelName, useProfile, useTeam } from '@front/model'
import { TeamMembers } from '../TeamMembers'
import { AddTeamButton } from '../addTeam/AddTeamButton'
import { MaterialSymbolIcon } from '../../terros/icon/MaterialSymbolIcon'
import { TListItem, TPushPanel, TSectionList } from '../../terros'
import { TeamTitle } from './TeamTitle'
import { TeamDetails } from './TeamDetails'
import { HeaderActions } from './HeaderActions'

type Props = {
  company?: CompanyData
  team?: TinyTeam
  onNavigateTeam: (teamId: TeamId) => void
  onClose: VoidFunction
  opened: boolean
}

export type TeamViewTab = 'users' | 'details' | 'preferences' | 'permissions'

export const DEFAULT_TEAM_VIEW_TAB: TeamViewTab = 'users'

export const TEAM_VIEW_TABS: TSegmentedControlOption<TeamViewTab>[] = [
  { label: 'Users', value: 'users', icon: 'group' },
  { label: 'Details', value: 'details', icon: 'info' },
  { label: 'Preferences', value: 'preferences', icon: 'settings' },
  { label: 'Permissions', value: 'permissions', icon: 'shield' },
]

export const useTeamViewTab = (): UseQueryStateReturn<TeamViewTab, TeamViewTab> =>
  useQueryState('tab', parseAsStringLiteral(TEAM_VIEW_TABS.map((tab) => tab.value)).withDefault(DEFAULT_TEAM_VIEW_TAB))

export const TEAM_VIEW_PANEL_WIDTH = 640

export const ViewTeamPanel = ({ team: viewingTeam, onNavigateTeam, onClose, opened }: Props): ReactElement => {
  const [activeTab, setActiveTab] = useState<TeamViewTab>(DEFAULT_TEAM_VIEW_TAB)

  const wasOpened = useRef(opened)
  useEffect(() => {
    if (opened && !wasOpened.current) setActiveTab(DEFAULT_TEAM_VIEW_TAB)
    wasOpened.current = opened
  }, [opened])

  return (
    <TPushPanel
      headerActions={<HeaderActions team={viewingTeam} />}
      opened={opened}
      title={viewingTeam ? <TeamTitle team={viewingTeam} /> : 'Team'}
      width={TEAM_VIEW_PANEL_WIDTH}
      onClose={onClose}
    >
      {viewingTeam && (
        <TeamViewContent
          activeTab={activeTab}
          teamId={viewingTeam.teamId}
          onNavigateTeam={onNavigateTeam}
          onTabChange={setActiveTab}
        />
      )}
    </TPushPanel>
  )
}

type TeamViewContentProps = {
  activeTab: TeamViewTab
  teamId: TeamId
  onNavigateTeam: (teamId: TeamId) => void
  onTabChange: (tab: TeamViewTab) => void
}

export const TeamViewContent = ({
  activeTab,
  teamId,
  onNavigateTeam,
  onTabChange,
}: TeamViewContentProps): ReactElement => {
  const { company } = useProfile()
  const { data, isLoading, error } = useTeam(teamId)
  if (isLoading || error || !data) return <NotReady data={data} error={error} id='team' isLoading={isLoading} />
  const { team, members, childTeams } = data
  return (
    <Stack gap='xl'>
      <EntityTabs options={TEAM_VIEW_TABS} value={activeTab} onChange={onTabChange} />
      {activeTab === 'users' && (
        <Stack gap='xl'>
          <TeamMembers isArchived={team.isDeleted === true} members={members} teamId={teamId} />
          <Subteams childTeams={childTeams} company={company} team={team} onNavigateTeam={onNavigateTeam} />
        </Stack>
      )}
      {activeTab === 'details' && <TeamDetails team={team} />}
      {activeTab === 'preferences' && <PreferencesForm entityId={team.teamId} readOnly={team.isDeleted} />}
      {activeTab === 'permissions' && (
        <PermissionContent entityId={team.teamId} readOnly={team.isDeleted} stickyToolbar={false} />
      )}
    </Stack>
  )
}

type SubteamsProps = {
  company?: CompanyData
  team: TeamData
  childTeams: TinyTeam[]
  onNavigateTeam: (teamId: TeamId) => void
}

export const Subteams = ({ company, team, childTeams, onNavigateTeam }: SubteamsProps): ReactNode => {
  if (team.level === company?.lowestTeamLevel && isEmpty(childTeams)) return
  const subteamName = getLevelName(company, team.level - 1)
  const pluralizedSubteamName = pluralize(childTeams, subteamName)
  const lowestTeamLevel = company?.lowestTeamLevel ?? 2
  const canAddChildTeam = !team.isDeleted && lowestTeamLevel < team.level

  const sortedChildTeams = [...childTeams].sort((a, b) => a.name.localeCompare(b.name))

  return (
    <TSectionList
      heading={{
        title: pluralizedSubteamName,
        children: canAddChildTeam && (
          <PermissionTooltip permissionType='team:update' targetId={team.teamId}>
            <AddTeamButton level={(team.level - 1) as TeamLevel} parentId={team.teamId} variant='ghost' />
          </PermissionTooltip>
        ),
      }}
    >
      {sortedChildTeams.map((t) => (
        <SubteamItem key={t.teamId} team={t} onNavigateTeam={onNavigateTeam} />
      ))}
      {isEmpty(childTeams) && <Alert icon={<MaterialSymbolIcon icon='info' />}>No {pluralizedSubteamName}</Alert>}
    </TSectionList>
  )
}

const SubteamItem = ({
  team,
  onNavigateTeam,
}: {
  team: TinyTeam
  onNavigateTeam: (teamId: TeamId) => void
}): ReactNode => {
  return (
    <TListItem
      onClick={() => onNavigateTeam(team.teamId)}
      rightSection={<TButton buttonType='icon-full' icon='chevron_forward' variant='gray' />}
      tooltip='View team'
    >
      <PrefetchedTeamInfo linked={false} size='md' team={team} />
    </TListItem>
  )
}
